import { useTranslation } from 'react-i18next';
import { Badge, Box, HStack, Stat, Text, VStack } from '@chakra-ui/react';
import type { ScimSyncStatusResponse } from '../../../services';

/**
 * Status panel: aggregate sync status (last_sync, sync_status, users_synced,
 * failures_last_hour). The manual Sync action lives in the drawer footer
 * — this panel carries no "Sync now" button of its own.
 *
 * Sync logs themselves are NOT exposed — the API returns aggregate
 * counts only. Sync-log detail lives in Django admin for engineering use.
 */

export interface StatusPanelProps {
  status: ScimSyncStatusResponse | undefined;
  isLoading: boolean;
}

export function StatusPanel({ status, isLoading }: StatusPanelProps) {
  const { t } = useTranslation();

  const renderBadge = (syncStatus: string | undefined) => {
    switch (syncStatus) {
      case 'success':
        return <Badge colorPalette="green">{t('sso.success', 'Success')}</Badge>;
      case 'failed':
        return <Badge colorPalette="red">{t('sso.failed', 'Failed')}</Badge>;
      case 'pending':
      default:
        return <Badge colorPalette="gray">{t('sso.pending', 'Pending')}</Badge>;
    }
  };

  return (
    <VStack align="stretch" gap={4}>
      <HStack gap={6}>
        <Stat.Root>
          <Stat.Label>{t('sso.lastSync', 'Last sync')}</Stat.Label>
          <Stat.ValueText fontSize="md">
            {status?.last_sync
              ? new Date(status.last_sync).toLocaleString()
              : t('sso.never', 'Never')}
          </Stat.ValueText>
        </Stat.Root>
        <Stat.Root>
          <Stat.Label>{t('sso.status', 'Status')}</Stat.Label>
          <Box mt={1}>{renderBadge(status?.sync_status)}</Box>
        </Stat.Root>
        <Stat.Root>
          <Stat.Label>{t('sso.usersSynced', 'Users synced')}</Stat.Label>
          <Stat.ValueText fontSize="md">
            {status ? status.users_synced : '—'}
          </Stat.ValueText>
        </Stat.Root>
        <Stat.Root>
          <Stat.Label>{t('sso.failuresLastHour', 'Failures (last hour)')}</Stat.Label>
          <Stat.ValueText
            fontSize="md"
            color={(status?.failures_last_hour ?? 0) > 0 ? 'red.600' : undefined}
          >
            {status ? status.failures_last_hour : '—'}
          </Stat.ValueText>
        </Stat.Root>
      </HStack>

      {isLoading ? (
        <Text fontSize="sm" color="text.secondary">
          {t('sso.loadingStatus', 'Loading status…')}
        </Text>
      ) : null}
    </VStack>
  );
}
